import React, { useState } from "react";
import {
	GridColDef,
	GridRenderCellParams,
	GridValueGetterParams,
} from "@mui/x-data-grid";
import moment from "moment";
import {Breadcrumbs, Button, MenuItem, Typography} from "@mui/material";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {GET_MAIL_SETS} from "../../graphql/queries/gqlGetMailSets";
import {MailexMails} from "./MailexMails";
import {PositionedMenu} from "../../../../components/common/PositionedMenu";
import {DELETE_MAIL_SETS} from "../../graphql/mutations/gqlDeleteMailSet";
import {MailSet} from "../../models/MailSet";
import {SCHEDULE} from "../../graphql/mutations/gqlSchedule";
import {toast} from "react-toastify";
import {GET_ERRORS} from "../../graphql/queries/gqlGetErrors";
import {download} from "../../helpers/FileHelper";
import {GenericModal} from "../GenericModal/GenericModal";
import {ModifyMail} from "../modify-mail/ModifyMail";
import {PROCESS_MAIL_SET} from "../../graphql/mutations/gqlProcessMailSet";
import {REPROCESS_ERRORS} from "../../graphql/mutations/gqlReprocessErrors";
import {GraphQlDataGrid} from "../../../../components/common/grid/GraphQlDataGrid";

interface IMailexSetsProps {
	setLoading: (value: boolean) => void;
}

export const MailexSets: React.FC<IMailexSetsProps> = ({ setLoading }) => {
	const [selectedMailSet, setSelectedMailSet] = useState<MailSet>();
	const [modifyMailSet, setModifyMailSet] = useState<MailSet>();
	const [refetchCounter, setRefetchCounter] = useState(0);

	const refetch = () => {
		setRefetchCounter(refetchCounter + 1);
	};

	const schedule = (mailSet: MailSet, scheduledDateTime?: Date) => {
		setLoading(true);
		scheduledDateTime ??= new Date(mailSet.scheduledDateTime);
		graphQLServices.mailexClient
			.mutate({
				mutation: SCHEDULE,
				variables: {
					mailSetId: mailSet.id,
					scheduledDateTime: scheduledDateTime,
				},
			})
			.then(async (result) => {
				const success = result.data.schedule.success;
				if (success) {
					refetch();
					toast.success("Scheduled");
				} else {
					toast.error(result.data.schedule?.errors[0] ?? "Failed");
				}
			})
			.finally(() => setLoading(false));
	};

	const process = (mailSetId: string) => {
		setLoading(true);
		graphQLServices.mailexClient
			.mutate({
				mutation: PROCESS_MAIL_SET,
				variables: {
					mailSetId: mailSetId,
				},
			})
			.then(async (result) => {
				const success = result.data.process.success;
				if (success) {
					refetch();
					toast.success("Scheduled");
				} else {
					result.data.process?.errors?.forEach((error: string) =>
						toast.error(error)
					);
				}
			})
			.finally(() => setLoading(false));
	};

	const reprocessErrors = (mailSetId: string) => {
		setLoading(true);
		graphQLServices.mailexClient
			.mutate({
				mutation: REPROCESS_ERRORS,
				variables: {
					mailSetId: mailSetId,
				},
			})
			.then(async (result) => {
				const success = result.data.reprocessErrors.success;
				if (success) {
					refetch();
					toast.success("Processing");
				} else {
					result.data.reprocessErrors?.errors?.forEach((error: string) =>
						toast.error(error)
					);
				}
			})
			.finally(() => setLoading(false));
	};

	const deleteMailSet = (mailSetId: string) => {
		setLoading(true);
		graphQLServices.mailexClient
			.mutate({
				mutation: DELETE_MAIL_SETS,
				variables: {
					mailSetId: mailSetId,
				},
			})
			.then(async (result) => {
				const success = result.data.deleteMailSet.success;
				if (success) {
					refetch();
					toast.success("Deleted");
				} else {
					result.data.deleteMailSet?.errors?.forEach((error: string) =>
						toast.error(error)
					);
				}
			})
			.finally(() => setLoading(false));
	};

	const downloadErrors = (mailSetId: string) => {
		setLoading(true);
		graphQLServices.mailexClient
			.query({
				query: GET_ERRORS,
				variables: {
					mailSetId: mailSetId,
				},
			})
			.then(async (result: any) => {
				await download(result.data.errors, `${mailSetId}.zip`);
			})
			.finally(() => setLoading(false));
	};

	const columns: GridColDef[] = [
		{
			field: "details",
			headerName: "View Details",
			width: 100,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<Button
						variant="contained"
						onClick={() => setSelectedMailSet({ ...params.row })}
					>
						View
					</Button>
				);
			},
		},
		{ field: "id", headerName: "ID", width: 70 },
		{ field: "description", headerName: "Description", width: 200 },
		{
			field: "metaData.fileName",
			headerName: "File Name",
			width: 350,
			valueGetter: (params) => {
				return params.row.metaData?.fileName;
			},
		},
		{ field: "status", headerName: "Status", width: 180 },
		{
			field: "failedMailCount",
			headerName: "Failed",
			width: 80,
			type: "number",
		},
		{
			field: "deliveredMailCount",
			headerName: "Delivered",
			width: 80,
			type: "number",
		},
		{
			field: "totalMailCount",
			headerName: "Total",
			width: 80,
			type: "number",
		},
		{
			field: "scheduledDateTime",
			headerName: "Scheduled",
			width: 200,
			valueGetter: (params: GridValueGetterParams) => {
				return moment(params.row.scheduledDateTime).format(
					"yyyy/MM/DD HH:mm (ddd)"
				);
			},
		},
		{
			field: "actions",
			headerName: "Actions",
			width: 100,
			renderCell: (params: GridRenderCellParams) => {
				return (
					<PositionedMenu buttonTitle={"Actions"}>
						<MenuItem onClick={() => process(params.row.id)}>Process</MenuItem>
						<MenuItem onClick={() => reprocessErrors(params.row.id)}>
							Reprocess Errors
						</MenuItem>
						<MenuItem onClick={() => schedule({ ...params.row })}>
							Schedule anyway
						</MenuItem>
						<MenuItem onClick={() => setModifyMailSet({ ...params.row })}>
							Modify
						</MenuItem>
						<MenuItem onClick={() => deleteMailSet(params.row.id)}>
							Delete
						</MenuItem>
						<MenuItem onClick={() => downloadErrors(params.row.id)}>
							Download errors
						</MenuItem>
					</PositionedMenu>
				);
			},
		},
	];

	return (
		<div style={{ height: "100%", width: "100%" }}>
			{!selectedMailSet && (
				<GraphQlDataGrid
					rows={[]}
					query={GET_MAIL_SETS}
					graphFunction={"mailSets"}
					client={graphQLServices.mailexClient}
					initialPageSize={10}
					rowsPerPageOptions={[10, 15, 20, 25, 50]}
					columns={columns}
					queryVariables={{}}
					defaultSortModel={[
						{
							field: "scheduledDateTime",
							sort: "desc",
						},
					]}
					defaultFilterModel={{
						items: [
							{
								field: "status",
								operator: "not",
								value: "COMPLETED",
							},
						],
					}}
				/>
			)}
			{selectedMailSet && (
				<>
					<Breadcrumbs aria-label="breadcrumb">
						<Typography color="text.primary">Mail Sets</Typography>
						{selectedMailSet && (
							<Typography color="text.primary">
								{moment(selectedMailSet.scheduledDateTime).format(
									"yyyy-MM-DD (ddd)"
								)}
							</Typography>
						)}
						{selectedMailSet && (
							<Typography color="text.primary">
								{selectedMailSet.description}
							</Typography>
						)}
						{selectedMailSet && (
							<Typography color="text.primary">
								{selectedMailSet.metaData?.fileName}
							</Typography>
						)}
					</Breadcrumbs>
					<MailexMails setId={selectedMailSet.id} setSet={setSelectedMailSet} />
				</>
			)}

			{modifyMailSet && (
				<GenericModal
					open={!!modifyMailSet}
					onClose={() => setModifyMailSet(undefined)}
					title={"Modify Mail Set"}
				>
					<ModifyMail mailSet={modifyMailSet} />
				</GenericModal>
			)}
		</div>
	);
};
